/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import {
	getCreatorDetailsPageModelsService,
	getModelDetailsPagePromptsService,
	getModelDetailsService,
	getModelsService,
	getPromptDetailsService,
	getPromptsService,
	likeDislikePromptService,
} from 'api/services/HomeService';
import { getPublicProfile } from 'api/services/ProfileService';
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { AspectRatio } from 'core/constants/enums/AspectRatioEnum.ts';
import { AuthenticationStatus } from 'core/constants/enums/AuthenticationStatus.ts';
import { GenerateSource } from 'core/constants/enums/GenerateSource.ts';
import { HomeFeaturesTab } from 'core/constants/enums/HomeFeaturesTab.ts';
import { HomeTabs } from 'core/constants/enums/HomeTabs.ts';
import { PromptsModalType } from 'core/constants/enums/PromptsModalType.ts';
import { RendernetErrors } from 'core/constants/enums/RendernetErrors.ts';
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import { sendToast } from 'core/layouts/components/toast/ToastUtils';
import { calculatePageNumbers, handleSomethingWentWrongErrorWithToast } from 'core/utils/utils';
import {
	getIsUserAuthenticatedSelector,
	listPromptHistorySelector,
	userPromptHistorySelector,
} from '../profileReducer/ProfileSlice';

const initialState = {
	currentHomeTab: HomeTabs.PROMPTS,

	prompts: [],
	randomisedPromptsIds: [],
	getPromptsAPIStage: ApiStage.IDLE,

	promptsTotalPages: 1,
	promptsCurrentPage: 1,

	likeDislikePromptAPIStage: ApiStage.IDLE,

	promptModalType: null,
	openPromptsModal: false,
	selectedPromptCategory: null,
	selectedPromptRandomisedIndex: null,

	models: [],
	getModelsAPIStage: ApiStage.LOADING,

	modelDetails: {
		prompts: {},
	},
	getModelDetailsAPIStage: ApiStage.LOADING,
	getModelDetailsPromptsAPIStage: ApiStage.LOADING,
	modelPromptsIds: [],
	selectedModelsPromptIndex: null,
	selectedProfilePagePromptIndex: null,

	creatorDetails: { models: [] },
	getCreatorDetailsAPIStage: ApiStage.LOADING,
	getCreatorDetailsModelsAPIStage: ApiStage.LOADING,

	promptDetails: {},
	getPromptDetailsAPIStage: ApiStage.LOADING,

	openUseCharacterModal: false,
	openSelectCanvasModal: false,
	openCreateNewCharacterModal: false,
	createNewCharacterModalSource: null,
	homePageTrueTouchModal: false,
	homePageUpscale8kModal: false,
	homePagePoseControlModal: false,
	homePageFaceSwapModal: false,
	homeNarratorModal: false,
	homePageVideoAnyoneModal: false,
	homePageFluxCharacterSelectModal: false,
	deleteAccountModalSource: false,
	homeGenerateImage: null,
	openStopCharacterCreationModal: { open: false, isEditMode: false },
	openCharacterCreationSuccessModal: { source: null, createdCharacterName: null, open: false },
	openDeleteCharacterConfirmationModal: false,
	openEditCharacterConfirmationModal: false,
	scrollAmountRight: null,
	creatorNotFound: null,
	modelNotFound: null,
	openQuickStartModal: true,
	mediaGenerationTrigger: null,
	homePageVideoTutorialModal: false,
	promptInput: '',
	aspectRatioInput: AspectRatio.TALL,
	homePageFaceReplaceModal: false,
	openMusicVideoModal: false,
	homeClothChangeModal: false,
	homeProductPhotoModal: false,
	selectedFeatureTab: HomeFeaturesTab.CHARACTERS,
};

export const homeInitialState = initialState;

export const getPromptsAsync = createAsyncThunk('home/getPromptsAsync', async (page = 1) =>
	getPromptsService(page),
);
export const likeDislikePromptAsync = createAsyncThunk(
	'home/likeDislikePromptAsync',
	async ({ promptId, liked }) =>
		likeDislikePromptService({
			promptId,
			liked,
		}),
);

export const getModelsAsync = createAsyncThunk('home/getModelsAsync', async () =>
	getModelsService(),
);

export const getModelDetailsAsync = createAsyncThunk('home/getModelDetailsAsync', async (modelId) =>
	getModelDetailsService(modelId),
);
export const getModelDetailsPromptsAsync = createAsyncThunk(
	'home/getModelDetailsPromptsAsync',
	async (modelId) => getModelDetailsPagePromptsService(modelId),
);

export const getCreatorDetailsAsync = createAsyncThunk(
	'home/getCreatorDetailsAsync',
	async (creatorId) => getPublicProfile(creatorId),
);
export const getCreatorDetailsPageModelsAsync = createAsyncThunk(
	'home/getCreatorDetailsPageModelsAsync',
	async (creatorId) => getCreatorDetailsPageModelsService(creatorId),
);

export const getPromptDetailsAsync = createAsyncThunk(
	'home/getPromptDetailsAsync',
	async (promptId) => getPromptDetailsService(promptId),
);

export const homeSlice = createSlice({
	name: GenerateSource.HOME,
	initialState,
	reducers: {
		setHomePromptsAPIStage: (state, action) => {
			state.getPromptsAPIStage = action.payload;
		},
		setHomePromptsCurrentPage: (state, action) => {
			state.promptsCurrentPage = action.payload;
		},

		togglePromptsModal: (state, action) => {
			const { modalType, openModal, selectedIndex } = action.payload;

			state.openPromptsModal = openModal;

			if (openModal) {
				state.promptModalType = modalType;

				if (modalType === PromptsModalType.HOME_PROMPTS_MODAL) {
					state.selectedPromptRandomisedIndex = selectedIndex;
				} else if (modalType === PromptsModalType.MODEL_PAGE_PROMPTS_MODAL) {
					state.selectedModelsPromptIndex = selectedIndex;
				} else if (modalType === PromptsModalType.PROFILE_PAGE_PROMPTS_MODAL) {
					state.selectedProfilePagePromptIndex = selectedIndex;
				}
			}
		},

		setSelectedPromptRandomisedIndex: (state, action) => {
			const { newIndex } = action.payload;

			state.selectedPromptRandomisedIndex = newIndex;
		},
		setSelectedModelsPromptIndex: (state, action) => {
			state.selectedModelsPromptIndex = action.payload;
		},
		setSelectedProfilePagePromptIndex: (state, action) => {
			state.selectedProfilePagePromptIndex = action.payload;
		},
		toggleUseCharacterModal: (state, action) => {
			state.openUseCharacterModal = action.payload;
		},
		toggleSelectCanvasModal: (state, action) => {
			state.openSelectCanvasModal = action.payload;
		},
		toggleCreateNewCharacterModal: (state, action) => {
			state.openCreateNewCharacterModal = action.payload.open;
			state.createNewCharacterModalSource = action.payload.route;
		},
		toggleStopCharacterCreationModal: (state, action) => {
			state.openStopCharacterCreationModal = action.payload;
		},
		toggleCharacterCreationSuccessModal: (state, action) => {
			state.openCharacterCreationSuccessModal = action.payload;
		},
		toggleDeleteCharacterConfirmationModal: (state, action) => {
			state.openDeleteCharacterConfirmationModal = action.payload;
		},
		toggleEditCharacterConfirmationModal: (state, action) => {
			state.openEditCharacterConfirmationModal = action.payload;
		},
		toggleHomePageTrueTouchModal: (state, action) => {
			state.homePageTrueTouchModal = action.payload;
		},
		toggleHomePageUpscale8kModal: (state, action) => {
			state.homePageUpscale8kModal = action.payload;
		},
		toggleHomePagePoseControlModal: (state, action) => {
			state.homePagePoseControlModal = action.payload;
		},
		toggleHomePageFaceSwapModal: (state, action) => {
			state.homePageFaceSwapModal = action.payload;
		},
		toggleHomePageFaceReplaceModal: (state, action) => {
			state.homePageFaceReplaceModal = action.payload;
		},
		toggleHomeClothChangeModal: (state, action) => {
			state.homeClothChangeModal = action.payload;
		},
		toggleHomeProductPhotoModal: (state, action) => {
			state.homeProductPhotoModal = action.payload;
		},
		toggleHomePageFluxCharacterSelectModal: (state, action) => {
			state.homePageFluxCharacterSelectModal = action.payload;
		},
		toggleHomePageVideoAnyoneModal: (state, action) => {
			state.homePageVideoAnyoneModal = action.payload;
		},

		toggleHomePageNarratorModal: (state, action) => {
			state.homeNarratorModal = action.payload;
		},
		setScrollAmountRight: (state, action) => {
			state.scrollAmountRight = action.payload;
		},
		setHomeGenerateImage: (state, action) => {
			state.homeGenerateImage = action.payload;
		},
		toggleDeleteAccountModel: (state, action) => {
			state.deleteAccountModalSource = action.payload;
		},
		toggleOpenQuickStartModal: (state, action) => {
			state.openQuickStartModal = action.payload;
		},
		toggleHomePageVideoTutorialModal: (state, action) => {
			state.homePageVideoTutorialModal = action.payload;
		},
		setMediaGenerationTrigger: (state, action) => {
			state.mediaGenerationTrigger = action.payload;
		},
		setHomePageCharacterPromptInput: (state, action) => {
			state.promptInput = action.payload;
		},
		setHomePageAspectRatioInput: (state, action) => {
			state.aspectRatioInput = action.payload;
		},
		toggleMusicVideoModal: (state, action) => {
			state.openMusicVideoModal = action.payload;
		},
		resetHomeState: (state) => {
			Object.keys(initialState).forEach((key) => {
				state[key] = initialState[key];
			});
		},
		setSelectedFeatureTab: (state, action) => {
			state.selectedFeatureTab = action.payload;
		},
	},
	extraReducers: (builder) => {
		// get prompts
		builder.addCase(getPromptsAsync.pending, (state) => {
			state.getPromptsAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getPromptsAsync.fulfilled, (state, action) => {
			const { prompts, listSize, pageSize } = action.payload;

			if (Object.keys(prompts).length < 1) {
				state.getPromptsAPIStage = ApiStage.ERROR;
				sendToast(ToastType.REFRESH_ON_API_FAILURE);
			} else {
				const promptsRandomisedIds = Object.keys(prompts);
				promptsRandomisedIds.sort(() => 0.5 - Math.random());

				state.getPromptsAPIStage = ApiStage.SUCCESS;
				if (state.promptsCurrentPage === 1) {
					state.randomisedPromptsIds = promptsRandomisedIds;
					state.prompts = prompts;
				} else {
					state.prompts = { ...state.prompts, ...prompts };
					state.randomisedPromptsIds = [...state.randomisedPromptsIds, ...promptsRandomisedIds];
				}

				state.promptsTotalPages = calculatePageNumbers(listSize, pageSize);
				state.selectedPromptRandomisedIndex = null;
			}
		});
		builder.addCase(getPromptsAsync.rejected, (state) => {
			state.getPromptsAPIStage = ApiStage.ERROR;
			sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});

		// like/dislike prompts
		builder.addCase(likeDislikePromptAsync.pending, (state) => {
			state.likeDislikePromptAPIStage = ApiStage.LOADING;
		});
		builder.addCase(likeDislikePromptAsync.fulfilled, (state, action) => {
			const { promptId, liked, message } = action.payload;

			state.likeDislikePromptAPIStage = ApiStage.SUCCESS;

			if (message === 'success') {
				const promptsCopy = { ...state.prompts };

				if (Object.keys(promptsCopy).includes(promptId)) {
					promptsCopy[promptId].liked = liked;
					if (liked) {
						promptsCopy[promptId].likes += 1;
					} else {
						promptsCopy[promptId].likes -= 1;
					}
					state.prompts = promptsCopy;
				}
				const promptDetailsCopy = { ...state.promptDetails };

				if (Object.keys(promptDetailsCopy).length !== 0) {
					promptDetailsCopy.liked = liked;
					if (liked) {
						promptDetailsCopy.likes += 1;
					} else {
						promptDetailsCopy.likes -= 1;
					}
					state.promptDetails = promptDetailsCopy;
				}
			}
		});
		builder.addCase(likeDislikePromptAsync.rejected, (state, action) => {
			state.likeDislikePromptAPIStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// get models
		builder.addCase(getModelsAsync.pending, (state) => {
			state.getModelsAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getModelsAsync.fulfilled, (state, action) => {
			const response = action.payload;

			if (response.length < 1) {
				state.getModelsAPIStage = ApiStage.ERROR;
				sendToast(ToastType.REFRESH_ON_API_FAILURE);
			} else {
				state.getModelsAPIStage = ApiStage.SUCCESS;
			}

			state.models = response;
		});
		builder.addCase(getModelsAsync.rejected, (state) => {
			state.getPromptsAPIStage = ApiStage.ERROR;
			sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});

		// get model details
		builder.addCase(getModelDetailsAsync.pending, (state) => {
			state.getModelDetailsAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getModelDetailsAsync.fulfilled, (state, action) => {
			const response = action.payload;

			state.getModelDetailsAPIStage = ApiStage.SUCCESS;
			state.modelDetails = { prompts: state.modelDetails.prompts, ...response };
		});
		builder.addCase(getModelDetailsAsync.rejected, (state, action) => {
			state.getModelDetailsAPIStage = ApiStage.ERROR;
			if (action.error.code === RendernetErrors.MODEL_NOT_FOUND) {
				state.modelNotFound = true;
			} else sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});
		// get model details prompts
		builder.addCase(getModelDetailsPromptsAsync.pending, (state) => {
			state.getModelDetailsPromptsAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getModelDetailsPromptsAsync.fulfilled, (state, action) => {
			const response = action.payload;

			state.getModelDetailsPromptsAPIStage = ApiStage.SUCCESS;

			state.modelDetails = { ...state.modelDetails, prompts: response };
			state.modelPromptsIds = Object.keys(response);
		});
		builder.addCase(getModelDetailsPromptsAsync.rejected, (state) => {
			state.getModelDetailsPromptsAPIStage = ApiStage.ERROR;
			sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});

		// get creator details
		builder.addCase(getCreatorDetailsAsync.pending, (state) => {
			state.getCreatorDetailsAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getCreatorDetailsAsync.fulfilled, (state, action) => {
			const response = action.payload;

			state.getCreatorDetailsAPIStage = ApiStage.SUCCESS;
			state.creatorDetails = { models: state.creatorDetails.models, ...response };
		});
		builder.addCase(getCreatorDetailsAsync.rejected, (state, action) => {
			state.getCreatorDetailsAPIStage = ApiStage.ERROR;
			if (action.error.code === RendernetErrors.USER_NOT_FOUND) {
				state.creatorNotFound = true;
			} else sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});

		// get creator details page models
		builder.addCase(getCreatorDetailsPageModelsAsync.pending, (state) => {
			state.getCreatorDetailsModelsAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getCreatorDetailsPageModelsAsync.fulfilled, (state, action) => {
			const response = action.payload;

			state.getCreatorDetailsModelsAPIStage = ApiStage.SUCCESS;
			state.creatorDetails = { ...state.creatorDetails, models: response };
		});
		builder.addCase(getCreatorDetailsPageModelsAsync.rejected, (state) => {
			state.getCreatorDetailsModelsAPIStage = ApiStage.ERROR;
			sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});

		// get prompt details
		builder.addCase(getPromptDetailsAsync.pending, (state) => {
			state.getPromptDetailsAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getPromptDetailsAsync.fulfilled, (state, action) => {
			state.getPromptDetailsAPIStage = ApiStage.SUCCESS;
			state.promptDetails = action.payload;
		});
		builder.addCase(getPromptDetailsAsync.rejected, (state) => {
			state.getPromptDetailsAPIStage = ApiStage.ERROR;
			sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});
	},
});

export const {
	setHomePromptsAPIStage,
	setHomePromptsCurrentPage,
	togglePromptsModal,
	setSelectedPromptRandomisedIndex,
	setSelectedModelsPromptIndex,
	setSelectedProfilePagePromptIndex,
	toggleUseCharacterModal,
	toggleSelectCanvasModal,
	toggleCreateNewCharacterModal,
	toggleStopCharacterCreationModal,
	toggleCharacterCreationSuccessModal,
	toggleDeleteCharacterConfirmationModal,
	toggleEditCharacterConfirmationModal,
	toggleHomePageTrueTouchModal,
	toggleHomePageUpscale8kModal,
	toggleHomePagePoseControlModal,
	toggleHomePageFaceSwapModal,
	toggleHomePageFluxCharacterSelectModal,
	toggleHomePageVideoAnyoneModal,
	toggleHomePageNarratorModal,
	toggleDeleteAccountModel,
	setScrollAmountRight,
	setHomeGenerateImage,
	toggleOpenQuickStartModal,
	toggleHomePageVideoTutorialModal,
	setMediaGenerationTrigger,
	setHomePageCharacterPromptInput,
	setHomePageAspectRatioInput,
	toggleHomeGenerationTrigger,
	toggleHomePageFaceReplaceModal,
	toggleHomeClothChangeModal,
	toggleHomeProductPhotoModal,
	toggleMusicVideoModal,
	resetHomeState,
	setSelectedFeatureTab,
} = homeSlice.actions;
export const promptModalTypeSelector = (state) => state.home.promptModalType;
export const openPromptsModalSelector = (state) => state.home.openPromptsModal;

export const promptsSelector = (state) => state.home.prompts;
export const randomisedPromptsIdsSelector = (state) => state.home.randomisedPromptsIds;
export const getPromptsAPIStageSelector = (state) => state.home.getPromptsAPIStage;

export const promptsCurrentPageSelector = (state) => state.home.promptsCurrentPage;
export const promptsTotalPagesSelector = (state) => state.home.promptsTotalPages;

export const likeDislikePromptAPIStageSelector = (state) => state.home.likeDislikePromptAPIStage;

export const selectedPromptRandomisedIndexSelector = (state) =>
	state.home.selectedPromptRandomisedIndex;
export const selectedPromptCategorySelector = (state) => state.home.selectedPromptCategory;
export const modelsSelector = (state) => state.home.models;
export const getModelsAPIStageSelector = (state) => state.home.getModelsAPIStage;

export const modelDetailsSelector = (state) => state.home.modelDetails;
export const getModelDetailsAPIStageSelector = (state) => state.home.getModelDetailsAPIStage;
export const getModelDetailsPromptsAPIStageSelector = (state) =>
	state.home.getModelDetailsPromptsAPIStage;
export const modelPromptsIdsSelector = (state) => state.home.modelPromptsIds;
export const selectedModelsPromptIndexSelector = (state) => state.home.selectedModelsPromptIndex;
export const selectedProfilePagePromptIndexSelector = (state) =>
	state.home.selectedProfilePagePromptIndex;

export const selectedPromptSelector = createSelector(
	[
		promptModalTypeSelector,

		promptsSelector,
		randomisedPromptsIdsSelector,
		selectedPromptRandomisedIndexSelector,
		selectedPromptCategorySelector,

		modelDetailsSelector,
		modelPromptsIdsSelector,
		selectedModelsPromptIndexSelector,

		selectedProfilePagePromptIndexSelector,
		getIsUserAuthenticatedSelector,
		userPromptHistorySelector,
		listPromptHistorySelector,
	],
	(
		promptModalType,
		//
		prompts,
		randomisedPromptsIds,
		selectedPromptRandomisedIndex,
		category,
		//
		modelDetails,
		modelPromptsIds,
		selectedModelsPromptIndex,
		//
		selectedProfilePagePromptIndex,
		isUserAuthenticated,
		userPromptHistory,
		listPromptHistory,
	) => {
		if (promptModalType === PromptsModalType.HOME_PROMPTS_MODAL) {
			return prompts[randomisedPromptsIds[selectedPromptRandomisedIndex]] ?? {};
		}
		if (promptModalType === PromptsModalType.MODEL_PAGE_PROMPTS_MODAL) {
			const { prompts: modelPrompts } = modelDetails;

			if (Object.keys(modelPrompts).length > 0 && selectedModelsPromptIndex !== null) {
				return modelPrompts[modelPromptsIds[selectedModelsPromptIndex]];
			}
		}
		if (promptModalType === PromptsModalType.PROFILE_PAGE_PROMPTS_MODAL) {
			const promptsList =
				isUserAuthenticated === AuthenticationStatus.AUTHENTICATED
					? Object.values(userPromptHistory)
					: Object.values(listPromptHistory);

			if (promptsList.length > 0 && selectedProfilePagePromptIndex !== null) {
				return promptsList[selectedProfilePagePromptIndex];
			}
		}
		return {};
	},
);

export const creatorDetailsSelector = (state) => state.home.creatorDetails;
export const getCreatorDetailsAPIStageSelector = (state) => state.home.getCreatorDetailsAPIStage;
export const getCreatorDetailsModelsAPIStageSelector = (state) =>
	state.home.getCreatorDetailsModelsAPIStage;

export const promptDetailsSelector = (state) => state.home.promptDetails;
export const getPromptDetailsAPIStageSelector = (state) => state.home.getPromptDetailsAPIStage;

export const openUseCharacterModalSelector = (state) => state.home.openUseCharacterModal;
export const openSelectCanvasModalSelector = (state) => state.home.openSelectCanvasModal;

export const openCharacterCreationSuccessModalSelector = (state) =>
	state.home.openCharacterCreationSuccessModal;

export const openCreateNewCharacterModalSelector = (state) =>
	state.home.openCreateNewCharacterModal;
export const createNewCharacterModalSourceSelector = (state) =>
	state.home.createNewCharacterModalSource;

export const createNewCharacterModalDetailsSelector = createSelector(
	[openCreateNewCharacterModalSelector, createNewCharacterModalSourceSelector],
	(open, route) => ({
		open,
		route,
	}),
);
export const homePageTrueTouchModelSelector = (state) => state.home.homePageTrueTouchModal;
export const homePageUpscale8kModelSelector = (state) => state.home.homePageUpscale8kModal;
export const homePagePoseControlSelector = (state) => state.home.homePagePoseControlModal;
export const homePageFaceSwapSelector = (state) => state.home.homePageFaceSwapModal;
export const homePageFaceReplaceModalSelector = (state) => state.home.homePageFaceReplaceModal;
export const homeClothChangeModalSelector = (state) => state.home.homeClothChangeModal;
export const homeProductPhotoModalSelector = (state) => state.home.homeProductPhotoModal;

export const homePageNarratorSelector = (state) => state.home.homeNarratorModal;

export const homePageFluxCharacterSelectModalSelector = (state) =>
	state.home.homePageFluxCharacterSelectModal;
export const homePageVideoAnyoneModalSelector = (state) => state.home.homePageVideoAnyoneModal;
export const homeGenerateImageSelector = (state) => state.home.homeGenerateImage;
export const homeScrollAmountSelector = (state) => state.home.scrollAmountRight;

export const deleteAccountModalSelector = (state) => state.home.deleteAccountModalSource;
export const creatorNotFoundSelector = (state) => state.home.creatorNotFound;
export const modelNotFoundSelector = (state) => state.home.modelNotFound;
export const QuickStartModalSelector = (state) => state.home.openQuickStartModal;
export const homePageVideoTutorialModalSelector = (state) => state.home.homePageVideoTutorialModal;
export const mediaGenerationTriggerSelector = (state) => state.home.mediaGenerationTrigger;
export const homePageCharacterPromptInputSelector = (state) => state.home.promptInput;
export const homePageAspectRatioInputSelector = (state) => state.home.aspectRatioInput;
export const toggleMusicVideoModalSelector = (state) => state.home.openMusicVideoModal;
export const selectedFeatureTabSelector = (state) => state.home.selectedFeatureTab;

export default homeSlice.reducer;
